import Scrollbar from "@@/components/Scrollbar";
import Box from "@@/elements/Box";
import { ArrowRight, Close } from "@@/elements/Icons/icons";
import Typography from "@@/elements/Typography";
import { useFocusReturn, useFocusTrap } from "@@/hooks/use-focus-trap";
import { useScrollLock } from "@@/hooks/use-scroll-lock";
import cn from "classnames";
import { RefObject, useEffect, useRef } from "react";
import TextButtonIcon from "../../Button/TextButtonIcon";
import { LanguageSelectorProps } from "../types";
import styles from "./index.module.scss";

type MarketProps = {
  closeModal?: () => void;
  open: boolean;
  addTracking?: boolean;
  triggerRef: RefObject<HTMLButtonElement>;
  modalId: string;
};

type Props = LanguageSelectorProps & MarketProps;

const HEADING_ID = "market-selector-heading";

const MarketSelectorModal = ({
  logoUrl,
  logoAltText,
  header,
  items,
  closeModalLabel,
  closeModal,
  open,
  addTracking,
  triggerRef,
  modalId,
}: Props) => {
  const { lock, unlock } = useScrollLock();
  const modalRef = useRef<HTMLDivElement>(null);

  useFocusReturn({
    containerRef: modalRef,
    triggerRef,
    isActive: open,
    onEscape: closeModal,
  });

  useFocusTrap({
    containerRef: modalRef,
    isActive: open,
  });

  useEffect(() => {
    if (open) {
      lock();
      setTimeout(() => {
        const firstFocusable = modalRef.current?.querySelector<HTMLElement>(
          "button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])"
        );
        firstFocusable?.focus();
      }, 100);
    } else {
      unlock();
    }
  }, [open]);

  const handleCloseModal = () => {
    closeModal?.();
    triggerRef.current?.focus();
  };

  return (
    <>
      <div
        aria-hidden="true"
        onClick={handleCloseModal}
        className={cn(styles.overlay, { [styles.open]: open })}
      />
      <div
        ref={modalRef}
        id={modalId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={HEADING_ID}
        tabIndex={-1}
        className={cn(styles.modal, { [styles.open]: open })}
        inert={!open ? "" : undefined}
      >
        <Box
          px={10}
          pt={10}
          pb={18}
          backgroundColor="White"
        >
          <img
            className={styles.logo}
            src={logoUrl}
            alt={logoAltText} />
          <Box
            width="full"
            flexDirection="column"
            alignItems="center">
            {header && (
              <Box pb={8}>
                <Typography
                  variant="heading5"
                  asElement="div"
                  id={HEADING_ID}>
                  {header}
                </Typography>
                <hr className={styles.line} />
              </Box>
            )}

            <Scrollbar className={styles.scrollarea}>
              <ul
                id="market-list"
                className={styles.list}>
                {items?.map((item, index) => (
                  <li
                    key={index}
                    className={styles.list_item}>
                    {item.active ? (
                      <a
                        aria-current="page"
                        className={styles.active_item}
                        href={item.link.href}
                        target={item.link.target}
                        title={item.link.title}
                        data-menu-type={addTracking ? "menu utilities" : undefined}
                        data-menu-level={addTracking ? "2" : undefined}
                        data-menu-text={addTracking ? item.link.text : undefined}
                        data-menu-path={addTracking ? item.link.href : undefined}
                      >
                        <div className={styles.icon_container}>
                          <ArrowRight />
                        </div>
                        <Typography
                          variant="heading5"
                          asElement="div">
                          {item.link.text}
                        </Typography>
                      </a>
                    ) : (
                      <a
                        href={item.link.href}
                        target={item.link.target}
                        title={item.link.title}
                        data-menu-type={addTracking ? "menu utilities" : undefined}
                        data-menu-level={addTracking ? "2" : undefined}
                        data-menu-text={addTracking ? item.link.text : undefined}
                        data-menu-path={addTracking ? item.link.href : undefined}
                      >
                        <Typography
                          className={styles.item}
                          color="ComplementaryCharcoal"
                          variant="heading5"
                          asElement="div"
                        >
                          {item.link.text}
                        </Typography>
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </Scrollbar>
          </Box>
          <div className={styles.close_button}>
            <TextButtonIcon
              aria-label={closeModalLabel}
              onClick={handleCloseModal}
              direction="right"
              dense
              label={closeModalLabel}
            >
              <Close />
            </TextButtonIcon>
          </div>
        </Box>
      </div>
    </>
  );
};

export default MarketSelectorModal;
